import {
	Component,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule } from '@angular/forms';
import {Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription, timer as observableTimer} from 'rxjs';

import {debounce} from 'rxjs/operators';
import {FeatureGroup} from '../../generic/classes/featuregroup';
import {FilterState} from '../../generic/classes/filter.state';
import {Product} from '../../generic/classes/product';
import {ProductPage} from '../../generic/classes/product.page';
import {ProductItemCommonComponent} from '../../generic/component/products/productitem.common.component';
import {ProductItemGridComponent} from '../../generic/component/products/productitem.grid.component';
import {ConfigurationHelper} from '../../generic/helper/configuration.helper';
import {Logger} from '../../generic/logging/default-log.service';
import {ActiveOrderService} from '../../generic/service/active.order.service';
import {ApplicationService} from '../../generic/service/application.service';
import {FilterService} from '../../generic/service/filter.service';
import {PlantscapingService} from '../../generic/service/plantscaping.service';
import {ProductService} from '../../generic/service/product.service';
import {UserService} from '../../generic/service/user.service';
import {WindowRef} from '../../generic/service/window-ref.service';
import {AssortmentBannerService} from '../../service/assortment.banner.service';
import {SelectedFiltersComponentComponent} from '../filters/selected.filters.component';
import {ProductItemListComponent} from './productitem.list.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { AddToShoppingcartAnimationComponent } from './add.to.shoppingcart.animation.component';
import { ScrollTopComponent } from './scroll.to.top.component';
import { AssortmentBannerComponent } from './banners/assortment.banner.component';
import { LazyLoadDirective } from '../../generic/component/lazyload/ng-lazy-load.directive';
import { ProductPaginationComponent } from './product.pagination.component';
import { ProductsSortComponent } from './products.sort.component';
import { ProductsScannerComponent } from './products.scanner.component';
import { FilterSearchComponent } from '../filters/filter.search.component';
import { FilterBarComponent } from '../filters/filterbar.component';
import { ProductsSearchLoadingComponent } from './products.search.loading.component';
import { NgClass } from '@angular/common';

@Component({
    selector: 'products-list',
    templateUrl: 'products.component.html',
    styleUrls: [
        './products.component.scss',
        '../../../assets/style/_overview-bar.scss',
        '../../../assets/style/_layout.scss',
    ],
    standalone: true,
    imports: [
        NgClass,
        ProductsSearchLoadingComponent,
        FilterBarComponent,
        FilterSearchComponent,
        ProductsScannerComponent,
        FormsModule,
        ProductsSortComponent,
        SelectedFiltersComponentComponent,
        ProductPaginationComponent,
        ProductItemListComponent,
        LazyLoadDirective,
        AssortmentBannerComponent,
        ProductItemGridComponent,
        ScrollTopComponent,
        AddToShoppingcartAnimationComponent,
        NgxPaginationModule,
    ],
})
export class ProductsComponent implements OnInit, OnDestroy {
	private static winRef: WindowRef = new WindowRef();
	private static CLEAR_FEATURE_FILTER_ON: number = 1;

	filterState: FilterState;
	scrollToId: string | null;
	loading: boolean = true;
	searchLoading: boolean = false;
	forceGridView: boolean = false;
	showFilters: boolean = false;
	hasFooter: boolean = ConfigurationHelper.getSettings().kvkShop;
	// back to the overview, the fragment is preserved so we can jump back to the products position.
	preserveFragment: boolean = true;

	isLoggedIn: boolean;
	useFastCheckout: boolean;
	allowExtendedSearch: boolean;
	showBanners: boolean;
	bannerInterval: number;

	// Initially true, so that when navigating from the product detail,
	productPage: ProductPage;
	featureGroups: FeatureGroup[];

	paginationForm: UntypedFormGroup;
	@ViewChildren(ProductItemGridComponent)
	productGridChildren: QueryList<ProductItemGridComponent>;
	@ViewChildren(ProductItemListComponent)
	productListChildren: QueryList<ProductItemListComponent>;
	private lazyRenderingTimer;
	@ViewChild(SelectedFiltersComponentComponent)
	private selectedFiltersComponent: SelectedFiltersComponentComponent;
	private filterStateChangedSubscription: Subscription;
	private selectedOrderChangedSubscription: Subscription;
	private forceGridViewWidthMatch: MediaQueryList;
	private forceGridViewWidthMatchListener;

	constructor(
		private productService: ProductService,
		private filterService: FilterService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private activeOrderService: ActiveOrderService,
		private logger: Logger,
		private formBuilder: UntypedFormBuilder,
		private assortmentBannerService: AssortmentBannerService,
		private titleService: Title,
		private metaService: Meta,
		public applicationService: ApplicationService,
		public userService: UserService,
		private plantscapingService: PlantscapingService,
	) {
		this.isLoggedIn = this.userService.isLoggedIn();

		if (this.isLoggedIn) {
			this.useFastCheckout = this.userService.getUser().useFastCheckout;
			this.allowExtendedSearch =
				this.userService.getUser().extendedSearch;
			this.showBanners = this.userService.getUser().showBanners;
			this.bannerInterval = this.userService.getUser().bannerInterval;
		}

		// Make sure we have a filterstate
		this.filterState = filterService.getFilterState();

		const urlParams = new URLSearchParams(window.location.search);
		this.filterState.plantscaping =
			urlParams.get('plantscaping') === '1' ? 1 : 0;
		this.plantscapingService.setPlantscapingInSession(
			this.filterState.plantscaping,
		);

		// Added an initial debounce for working around the issue that the loadProducts is triggered
		// twice if there's an active order selected (on a page reload).
		// Caused by the code below, which calls the loadProducts
		// again, when an active order is resolved (async logic in the active order service).
		// This can't be easilly fixed and so we added a debounce, so that the first call is never executed.
		// If there's already an active 'selected order' it means, the selectedOrderChangedSubscription
		// will not be triggered and we don't have to add a debounce time.
		// Caution: the 'saved active order' may NOT resolve in an actual ActiveOrder
		// if the order is, for example, expired.
		let initialDebounceTime =
			activeOrderService.getSavedActiveOrder() != null &&
			!activeOrderService.hasSelectedOrder()
				? 500
				: 0;

		this.filterStateChangedSubscription =
			this.filterService.filterStateChanged
				.pipe(debounce(() => observableTimer(initialDebounceTime)))
				.subscribe(filterState => {
					this.loading = true;
					this.loadProducts();
					this.setMetaData(filterState);

					// Remove the debounce if the first necessary debounce is completed.
					initialDebounceTime = 0;
				});

		this.selectedOrderChangedSubscription =
			this.activeOrderService.selectedOrderChangedSubject.subscribe(() =>
				// Triggers the filterStateChanged subject, with the new active order in the filter state.
				filterService.notifyFilterStateChange(filterService.getFilterState()),
			);

		this.paginationForm = this.formBuilder.group({
			pageNumber: '',
		});
	}

	gridRowAmountOfColumns(): number {
		const numberOfColumns = getComputedStyle(
			document.documentElement,
		).getPropertyValue('--amount-of-grid-columns');
		return parseInt(numberOfColumns);
	}

	/**
	 * Is triggered from the product items components when the url fragment,
	 * matches the fragment of a specific product. When there's a match
	 * this function makes sure the browser jumps to the products page position.
	 *
	 * @param {string} anchorValue
	 */
	productMatchedFragmentChangeListener(anchorValue: string): void {
		if (!anchorValue) {
			return;
		}

		// After there is a match, the fragment should not be kept when navigating to a different (filter/page) URL.
		// Otherwise the page keeps jumping to a specific product when filter or page changes are triggered.
		this.preserveFragment = false;

		const element: HTMLElement = this.getAnchorElement(anchorValue);

		// Check to see if the user has filtered on "Stock" (VOORRAAD) products. This effects the scrollTo offset.
		// This is because on the stock filter every item is a stock item. But on other filters it might be mixed results
		// This means that without this filter we have differences in product row height.
		const isStockFilterSelected =
			this.filterState.features &&
			this.filterState.features.find(item => item === 'VOORRAAD');

		let offsetY = isStockFilterSelected ? 45 : 70;

		// When not logged in there are no Stock products and the view is always in grid mode
		// so we force a value of 10 to leave some space above the grid product item.
		if (!this.isLoggedIn) {
			offsetY = 10;
		}

		if (element && !this.loading) {
			setTimeout(() => {
				window.scrollTo(
					0,
					element.getBoundingClientRect().top - offsetY,
				);
			}, 700);
		}
	}

	getAnchorElement(anchorValue: string): HTMLElement {
		const anchorId = anchorValue.includes("&") ? anchorValue.split("&")[0] : anchorValue;

		const anchor = anchorId.startsWith('#')
			? anchorId
			: `#${anchorId}`;

		return document.querySelector(anchor) as HTMLElement;
	}

	ngOnDestroy(): void {
		this.filterStateChangedSubscription.unsubscribe();
		this.selectedOrderChangedSubscription.unsubscribe();
		this.clearLazyLoadingLogic();

		if (
			this.forceGridViewWidthMatch &&
			this.forceGridViewWidthMatchListener
		) {
			this.forceGridViewWidthMatch.removeListener(
				this.forceGridViewWidthMatchListener,
			);
		}
	}

	clearLazyLoadingLogic() {
		if (this.lazyRenderingTimer) {
			clearInterval(this.lazyRenderingTimer);
		}
	}

	getProductChildComponents(): QueryList<ProductItemCommonComponent> {
		return this.isListView()
			? this.productListChildren
			: this.productGridChildren;
	}

	ngOnInit() {
		this.activatedRoute.params.subscribe(params => {
			// Entry point to start a new order
			const newOrder = params.newOrder;
			if (newOrder) {
				this.applicationService.openPopup(
					this.applicationService.closePopup,
				);
			}

			// Entry point to select an active order
			const shipmentDate = params.activeOrderDate;
			const addressCode = decodeURIComponent(params.activeOrderCode);

			if (shipmentDate && addressCode) {
				this.activeOrderService.setOrderSelectedForShipmentDateAndDeliveryAddress(
					shipmentDate,
					addressCode,
				);
			}

			const openBarcodeScanner = params.origin;
			if (openBarcodeScanner) {
				this.applicationService.openBarcodeScannerPopup(
					this.applicationService.closeBarcodeScannerPopup,
				);
			}
		});

		this.activatedRoute.fragment.subscribe(fragment => {
			this.scrollToId = fragment;
		});

		this.initializeForceGridViewLogic();

		if (this.isLoggedIn && this.showBanners) {
			this.getAssortmentBannersForPage(this.filterState.plantscaping === 1 ? 1 : 0);
		}

		this.setMetaData();
	}

	setMetaData(
		filterState?: FilterState,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		featureGroups?: FeatureGroup[],
	): void {
		const shopTitle = ProductsComponent.winRef.nativeWindow.shopSettings.name
			? ProductsComponent.winRef.nativeWindow.shopSettings.name
			: 'Noviflora webshop';
		const allProductsTitle = $localize`:@@all.products.link:All products`;
		const allProductsDescription = $localize`:@@products.meta.description:${shopTitle} offers an extensive, high-quality assortment of green and flowering house and garden plants, as well as cut flowers.`;
		const allProductsKeywords = $localize`:@@products.meta.keywords:assortment, flowers, plants, local, international`;
		const productsTitle = `${allProductsTitle} | ${shopTitle}`;
		const productsTitleWithPossibleFilter = filterState
			? this.getTitleWithFilterState(
				shopTitle,
				allProductsTitle,
				filterState,
			)
			: productsTitle;

		this.titleService.setTitle(productsTitleWithPossibleFilter);

		this.metaService.updateTag({
			name: 'description',
			content: `${allProductsDescription}`,
		});

		this.metaService.updateTag({
			name: 'keywords',
			content: `${allProductsKeywords}`,
		});
	}

	getTitleWithFilterState(
		shopTitle: string,
		allProductsTitle: string,
		filterState: FilterState,
	): string {
		if (filterState && filterState.searchText) {
			const searchLabel = $localize`:@@filter.search.header:Search`;
			return `${searchLabel} ${filterState.searchText} - ${shopTitle}`;
		}

		if (filterState && filterState.priceFrom) {
			const priceFromLabel = $localize`:@@products.filters.selected.priceFrom.text:Price from:`;
			return `${priceFromLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		if (filterState && filterState.priceTo) {
			const priceToLabel = $localize`:@@products.filters.selected.priceTo.text:Price to:`;
			return `${priceToLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		if (filterState && filterState.potSizeFrom) {
			const potSizeFromLabel = $localize`:@@products.filters.selected.potSizeFrom.text:Pot size from:`;
			return `${potSizeFromLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		if (filterState && filterState.potSizeTo) {
			const potSizeToLabel = $localize`:@@products.filters.selected.potSizeTo.text:Pot size to:`;
			return `${potSizeToLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		if (filterState && filterState.heightFrom) {
			const heightFromLabel = $localize`:@@products.filters.selected.heightFrom.text:Height from:`;
			return `${heightFromLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		if (filterState && filterState.heightTo) {
			const heightToLabel = $localize`:@@products.filters.selected.heightTo.text:Height to:`;
			return `${heightToLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		if (filterState && filterState.grower) {
			const growerLabel = $localize`:@@product.feature.grower:Grower`;
			return `${growerLabel} - ${allProductsTitle} - ${shopTitle}`;
		}

		return `${allProductsTitle} | ${shopTitle}`;
	}

	toggleFilterbar() {
		this.showFilters = !this.showFilters;
	}

	getCurrentPageInput() {
		return this.paginationForm.get('pageNumber');
	}

	changePage(pageNumber) {
		this.filterService.changePage(pageNumber);
		this.getCurrentPageInput()?.setValue('');
	}

	getShipmentDate(): Date | null {
		const selectedOrder = this.activeOrderService.selectedOrder;
		return selectedOrder ? selectedOrder.shipmentDate : null;
	}

	isListView() {
		return (
			!this.forceGridView && this.applicationService.getProductsListView()
		);
	}

	toggleView(): void {
		const isListView = !this.isListView();
		this.applicationService.setProductsListView(isListView);
		this.startLazyRendering();
	}

	getGridViewClass(): string {
		return this.isListView() ? '' : 'active';
	}

	getListViewClass(): string {
		return this.isListView() ? 'active' : '';
	}

	triggerExtendedSearch(): void {
		this.filterService.setClearFeatureFilter(
			ProductsComponent.CLEAR_FEATURE_FILTER_ON,
		);
		this.searchLoading = true;
	}

	showExtendedSearch(): boolean {
		return (
			this.allowExtendedSearch &&
			this.isSearching() &&
			!this.filterState.clearFeatureFilter
		);
	}

	startLazyRendering() {
		// Clear old intervals to prevent multiple timers.
		this.clearLazyLoadingLogic();

		const startLazyRendering = () => {
			const notRenderedComponents =
				this.getProductChildComponents().filter(
					productComponent => !productComponent.show,
				);

			if (notRenderedComponents.length < 1) {
				this.clearLazyLoadingLogic();
				return;
			}

			notRenderedComponents
				.slice(0, 12)
				.forEach(productComponent => (productComponent.show = true));
		};

		// Directly start rendering the first products.
		startLazyRendering();

		// And add an interval to lazy render the rest of the products.
		this.lazyRenderingTimer = setInterval(() => {
			startLazyRendering();
		}, 10);
	}

	loadProducts() {
		const filterState = this.filterService.getFilterState();

		this.clearLazyLoadingLogic();

		// Update the URL based on the current state.
		// So if the page is bookmarkable and can be shared.
		this.router.navigate(['products'], {
			queryParams: {
				page: filterState.page,
				pageSize: filterState.pageSize,
				features: filterState.features,
				searchText: filterState.searchText,
				sorting: filterState.sorting,
				heightFrom: filterState.heightFrom,
				heightTo: filterState.heightTo,
				potSizeFrom: filterState.potSizeFrom,
				potSizeTo: filterState.potSizeTo,
				priceFrom: filterState.priceFrom,
				priceTo: filterState.priceTo,
				clearFeatureFilter: filterState.clearFeatureFilter,
				grower: filterState.grower,
				itemNo: filterState.itemNo,
				plantscaping: filterState.plantscaping,
			},
			preserveFragment: this.preserveFragment,
			skipLocationChange: false,
		});

		this.productService
			.loadProductsAndFeatures(true)
			.then(result => {
				this.productPage = result.productPage;

				this.startLazyRendering();

				this.featureGroups = result.featureGroups;

				this.selectedFiltersComponent.setSelectedFilters(
					result.featureGroups,
				);

				setTimeout(() => {
					this.searchLoading = false;
				}, 5000);
			})
			.catch(error => {
				this.searchLoading = false;
				this.logger.error(
					'Error loading the products from the server',
					error,
				);
			})
			.finally(() => {
				this.loading = false;
				this.productMatchedFragmentChangeListener(window.location.hash);
			});
	}

	viewProductDetail(detailUrl, replaceUrl?: boolean) {
		if (replaceUrl) {
			this.router.navigateByUrl(detailUrl, {replaceUrl});
			return;
		}
		this.router.navigateByUrl(detailUrl);
	}

	getCorrectedPageNumber(totalPages) {
		// If the input is lower then or equal to 0
		// return the first page
		if (this.getCurrentPageInput()?.value <= 0) {
			return 1;
		}

		// If the input is higher then the total number of pages
		// return the last page
		if (this.getCurrentPageInput()?.value > totalPages) {
			return totalPages;
		}

		return this.getCurrentPageInput()?.value;
	}

	onChangePageSubmit(totalPages) {
		const pageNumber = this.getCorrectedPageNumber(totalPages);

		// Do nothing if the input field is empty
		if (pageNumber === null) {
			return;
		}

		this.changePage(pageNumber);
	}

	/**
	 * Trigger the retrieval of all Assortment Banners. We trigger that here so we only call this once during the
	 * creation of the pages.
	 */
	getAssortmentBannersForPage(isPlantscapingActive: number): void {
		this.assortmentBannerService.getAssortmentBanners(isPlantscapingActive);
	}

	/**
	 * Compares the product index to a specified amount to set the 'show' value for a product to true.
	 * This show value is used to lazy render the buy unit component.
	 *
	 * @param index
	 * @param amount
	 */
	isShownWhenIndexIsSmallerThan(index: number, amount: number): boolean {
		return index < amount;
	}

	/**
	 * This helps angular know which component in a for loop has changed
	 *
	 * @param index
	 * @param item
	 */
	public trackByFunction(index, item) {
		if (!item) {
			return null;
		}

		return item;
	}

	/**
	 * Check if the given product is on a grid row which contains one or more StockProducts.
	 *
	 * @param {Product} product to test
	 * @param {number} rowSize number of items on a grid row.
	 * @return {boolean} true if the given product is on a row with StockProducts.
	 */
	gridRowWithStockType(product: Product, rowSize: number): boolean {
		const indexProduct = this.productPage.content.indexOf(product); // array is 0 based.
		const indexRowStart = indexProduct - (indexProduct % rowSize);
		const indexRowEnd = indexRowStart + rowSize - 1;

		let gridRowHasStock: boolean = false;
		for (
			let indexOnRow = indexRowStart;
			indexOnRow <= indexRowEnd;
			indexOnRow += 1
		) {
			gridRowHasStock =
				gridRowHasStock ||
				this.isStockProduct(this.productPage.content[indexOnRow]);
		}
		return gridRowHasStock;
	}

	/**
	 * Check if the given product is on a grid row which contains one or more products on sale.
	 *
	 * @param {Product} product to test
	 * @param {number} rowSize number of items on a grid row.
	 * @return {boolean} true if the given product is on a row with sales products.
	 */
	gridRowWithSaleProducts(product: Product, rowSize: number): boolean {
		const indexProduct = this.productPage.content.indexOf(product); // array is 0 based.
		const indexRowStart = indexProduct - (indexProduct % rowSize);
		const indexRowEnd = indexRowStart + rowSize - 1;

		let gridRowHasSaleProducts: boolean = false;
		for (
			let indexOnRow = indexRowStart;
			indexOnRow <= indexRowEnd;
			indexOnRow += 1
		) {
			gridRowHasSaleProducts =
				gridRowHasSaleProducts ||
				this.isSalesProduct(this.productPage.content[indexOnRow]);
		}
		return gridRowHasSaleProducts;
	}

	private hasAvailableBanners(): boolean {
		return (
			this.assortmentBannerService.getBanners() &&
			this.assortmentBannerService.getBanners().length > 0
		);
	}

	/**
	 * Verify if we can show banners to a user and at what interval.
	 *
	 * @param index
	 */
	showBannerForIndex(index: number): boolean {
		return (
			this.getProductIndex(index) % this.bannerInterval ===
			this.bannerInterval - 1 &&
			this.showBanners &&
			this.hasAvailableBanners()
		);
	}

	/**
	 * Calculates the index by counting ahead for all pages. This we the interval remembers the position of the last
	 * banner on the previous page and counts from that point on instead of resetting the count/interval.
	 *
	 * @param index
	 */
	getProductIndex(index): number {
		if (this.productPage.number > 0) {
			return index + this.productPage.number * this.productPage.size;
		}

		return index;
	}

	/**
	 * Takes the product and sees if it is of type 'Stock'.
	 *
	 * @param product
	 * @return boolean
	 */
	isStockProduct(product: Product): boolean {
		if (!product) {
			return false;
		}

		return this.productService.isTypeStock(product);
	}

	/**
	 * Forces the view to the grid view. This is used for mobile screens where we force the
	 * view to be grid.
	 *
	 * @param force
	 */
	private setForceGridView(force) {
		this.forceGridView = force;
	}

	/**
	 * Initializes a listener which forces the grid product overview,
	 * when the viewport is smaller then 1025px.
	 */
	private initializeForceGridViewLogic() {
		if (!this.isLoggedIn) {
			this.forceGridView = true;
			return;
		}

		this.forceGridViewWidthMatch = window.matchMedia('(max-width: 1025px)');

		// Set initial state
		this.forceGridView = this.forceGridViewWidthMatch.matches;

		this.forceGridViewWidthMatchListener = () => {
			this.setForceGridView(this.forceGridViewWidthMatch.matches);
			this.startLazyRendering();
		};

		// Set state based on the 'state change' of the media query.
		// addListener is used for backwards compatibility. The deprecation notice is correct for modern browsers.
		// The support is sadly still needed since IE11 for example can't listen to the Event.addEventListener and
		// in turn uses 'attachEvent'. For the time being this code is correct and we can safely ignore the deprecation
		// warning. But we should keep a eye on this code.
		this.forceGridViewWidthMatch.addListener(
			this.forceGridViewWidthMatchListener,
		);
	}

	private isSearching(): boolean {
		return this.filterState.searchText !== undefined;
	}

	/**
	 * Takes the product and verifies if it is on 'Sale'.
	 *
	 * @param product
	 * @return boolean true is on sale
	 */
	private isSalesProduct(product: Product): boolean {
		if (!product) {
			return false;
		}

		return product.saleDiscount > 0;
	}
}
